// Copyright (c) Microsoft. All rights reserved.

import {
    Divider,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    shorthands,
    TagGroup,
} from '@fluentui/react-components';
import React from 'react';
import { ChatMessageType, IChatMessage } from '../../../libs/models/ChatMessage';
import { customTokens } from '../../../styles';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import {
    editConversationInput,
    updateBotResponseStatus,
} from '../../../redux/features/conversations/conversationsSlice';
import { addAlert } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import { GetResponseOptions, useChat } from '../../../libs/hooks/useChat';
import { ArrowReplyDown20Regular } from '@fluentui/react-icons';
import * as utils from './../../utils/TextUtils';

interface IFollowUpTagsProps {
    allowUsage: boolean;
    message: IChatMessage;
}

const useClasses = makeStyles({
    root: {
        marginTop: '-10px',
        flexWrap: 'wrap',
        ...shorthands.gap(customTokens.spacingHorizontalS),
    },
    tag: {
        ...shorthands.borderColor(customTokens.colorPaletteGreenBackground3),
        ':hover': {
            backgroundColor: customTokens.colorPaletteGreenBackground1,
        },
    },
});

export const FollowUpTags: React.FC<IFollowUpTagsProps> = ({ allowUsage, message }) => {
    const dispatch = useAppDispatch();
    const { selectedId } = useAppSelector((state: RootState) => state.conversations);

    const chat = useChat();
    const [, setShouldAutoScroll] = React.useState(true);

    const classes = useClasses();

    const followUps = utils.extractFollowUpRecommendations(message);

    if (followUps.length === 0) {
        return null;
    }

    const addMessage = async (options: GetResponseOptions) => {
        await chat.getResponse(options);
        setShouldAutoScroll(true);
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (!allowUsage) {
            return;
        }
        if (value.trim() === '') {
            return;
        }

        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Calling the kernel' }));
        addMessage({ value, messageType, chatId: selectedId }).catch((error) => {
            const message = `Error submitting chat input: ${(error as Error).message}`;
            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    return (
        <>
            <Divider alignContent="start">Folgefragen</Divider>
            <TagGroup className={classes.root}>
                {followUps.map((followUp, index) => {
                    return (
                        <InteractionTag
                            key={`followup-tag-${index}`}
                            shape="circular"
                            size="small"
                            onClick={() => {
                                handleSubmit(followUp);
                            }}
                        >
                            <InteractionTagPrimary className={classes.tag} icon={<ArrowReplyDown20Regular />}>
                                {followUp}
                            </InteractionTagPrimary>
                        </InteractionTag>
                    );
                })}
            </TagGroup>
        </>
    );
};
